import { FeatureFlagIds } from "./utils/featureFlagIds";
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from "react-redux";
import { doInitialLoad } from "./store/application/actions";
import { ILoggedInUser } from "./Interfaces/ILoggedInUser";
import {
  SET_LOGGED_IN_USER,
  SET_INITIAL_LOAD_COMPLETE,
  loadCurrentReleaseNotes, loadEmployeeApplicationHistories,
} from './store/application/types';
import { fetchData } from './Data/fetch';
import { parseIpBlockError } from './Data/IpWhitelistHandler';
import './App.scss';
import { MS_PER_MINUTE } from "@progress/kendo-date-math";
import { AppState } from './store/rootReducer';
import { loadApplicationNotifications } from "./store/Notifications/actions";
import { loadPageLayouts } from './store/preferences/actions';
import AuthService from './Services/AuthService';
import { UserSummary } from './Data/UserSummary';
import { RouterProvider } from 'react-router';
import Dashboard from './Components/Container/Dashboard/Dashboard';
import dashboardRoutes from './routes/dashboardRoutes';
import { createBrowserRouter } from 'react-router-dom';
import {numberOfEmployeeApplicationHistoriesToTrack} from "./utils/constants";
import GlobalSearch from './Components/GlobalSearch/GlobalSearch';
import { LoadingPanel, SoftwareUpdateNotifier } from '@amsanalytics/react-shared-library';
import UnauthorizedAccess from './Components/Views/UnauthorizedAccess';


const router = createBrowserRouter([
  {
    path: '/unauthorized',
    element: <UnauthorizedAccess />,
  },
  {
    path: '/',
    element : <Dashboard routes={dashboardRoutes} />,
    children: dashboardRoutes?.map((route, key) => {
      return(
        {
          path: route.path,
          element: <route.component />,
          key: key,
        }
      );
    })
  }
]);

function App(props: { authService: AuthService }) {
  const dispatch = useDispatch();
  const loadComplete: boolean = !useSelector((state: AppState) => state.applications.loadingInitialData);
  const currentUser = useSelector((state: AppState) => state.applications.loggedInUser);
  const ipBlocked = useSelector((state: AppState) => state.applications.ipBlocked);
  // curent release notes loading interval
  const currentReleaseNotesInterval = useRef<number>(-1);
  const isGloballySearching = useSelector((state: AppState) => state.applications.isGloballySearchingApps);
  const lookupsLoaded = useSelector((state: AppState) => state.applications.lookupsLoaded);
  props.authService.userSignedIn = (user: Partial<UserSummary>) => {
    const initialize = async () => {
      try {
        const user: ILoggedInUser = await fetchData(`api/employee/current`, dispatch);
        if (!(user as any).error) {
          dispatch({ type: SET_LOGGED_IN_USER, payload: user });
          if (user?.employee) {
            dispatch(doInitialLoad());
          } else {
            dispatch({ type: SET_INITIAL_LOAD_COMPLETE });
          }
        }
      } catch (error) {
        if (!parseIpBlockError(error)) {
          throw error;
        }
      }
    };
    initialize();
  };

  useEffect(() => {
    // Redirect to unauthorized page if IP is blocked
    if (ipBlocked && window.location.pathname !== '/unauthorized') {
      router.navigate('/unauthorized');
    }
  }, [ipBlocked]);

  useEffect(() => {
    // load current release notes immediately
    // Runs on first load
    if (currentUser && !ipBlocked) {
      dispatch(loadCurrentReleaseNotes());
      dispatch(loadApplicationNotifications());
      dispatch(loadPageLayouts());
      dispatch(loadEmployeeApplicationHistories(numberOfEmployeeApplicationHistoriesToTrack));
      // Then every ___ amount of time after thereafter
      let interval: number = -1;
      interval = window.setInterval(async () => {
        dispatch(loadApplicationNotifications());
      }, 1 * MS_PER_MINUTE); // 1 minute
      // load current release notes every 2 minutes
      currentReleaseNotesInterval.current = window.setInterval(async () => {
        dispatch(loadCurrentReleaseNotes());
      }, 2 * MS_PER_MINUTE);
      return () => {
        clearInterval(interval);
        clearInterval(currentReleaseNotesInterval.current);
      }
    }
  }, [currentUser, ipBlocked]);


  // // Remove 'Dashboard' route if feature flag is present.
  // if (configuration?.featureFlags?.[FeatureFlagIds.HideDashboardRoute] === true) {
  //   routes = reject((r: IDashboardRoute) => r.sidebarName.toLowerCase() === 'dashboard', routes);
  // }
  // if (configuration?.featureFlags?.[FeatureFlagIds.HideApplicationLink] === true) {
  //   routes = reject((r: IDashboardRoute) => r.sidebarName.toLowerCase() === 'application', routes);
  // }

  // useEffect(() => {
  //   const createHubConnection = async () => {
  //     if(connection || currentUser === undefined || currentUser === null || configuration === undefined) {
  //       return;
  //     }
  //     const chatHubConnectionString = `applicationHub`;
  //     const hubConnection = new signalR.HubConnectionBuilder().withUrl(chatHubConnectionString).build();
  //     try {
  //       if (hubConnection) {
  //         await hubConnection.start();
  //         console.log("Connection successful!");
  //         hubConnection.on("onClientConnected", (connectionId) => {
  //           hubConnection.invoke("UpdateConnectedUserInformation",
  //             configuration.clientId,
  //             currentUser.employee.id
  //           );
  //           dispatch(setSignalRConnectionId(connectionId));
  //         });
  //         hubConnection.on("onInitializeLockedApplications", (lockedApplications: IApplicationLockInfo[]) => {
  //           dispatch(loadLockedApplications(lockedApplications));
  //         });
  //         hubConnection.on("onApplicationLocked", (lockedApplication: IApplicationLockInfo) => {
  //           dispatch(lockApplication(lockedApplication));
  //         });
  //         hubConnection.on("onApplicationsUnlocked", (unlockedApplicationInfo: IApplicationUnlockInfo) => {
  //           dispatch(unlockApplications(unlockedApplicationInfo));
  //         });
  //         hubConnection.on("onApplicationSaved", (saveApplicationInfo: ApplicationSaveEvent) => {
  //           dispatch(setApplicationSaved(saveApplicationInfo));
  //         });

  //         dispatch(setHubConnection(hubConnection));
  //       }
  //     } catch (err) {
  //       console.log(err);
  //     }
  //     // setConnection(hubConnection);

  //   };
  //   createHubConnection();
  // }, [currentUser, configuration]);

  // return (<>
  //   {currentUser?.preventAccess === true
  //     ? <NotAuthorized />
  //     : loadComplete
  //       ? props.children
  //       : <CircularProgress style={{ marginLeft: "50%", marginTop: "20%" }} size={68} />
  //   }
  //   <Dialog
  //     open={!!authenticationTimedOut}
  //     onClose={() => {
  //       dispatch({ type: SET_AUTHENTICATION_TIMED_OUT, payload: false });
  //     }}
  //   >
  //     <DialogTitle>You have been logged out.</DialogTitle>
  //     <DialogContent>
  //       <DialogContentText>
  //         Please log back in to continue using the app.
  //       </DialogContentText>
  //     </DialogContent>
  //     <DialogActions>
  //       <Button color="inherit" onClick={() => {
  //         dispatch({ type: SET_AUTHENTICATION_TIMED_OUT, payload: false });
  //       }}>
  //         Close
  //       </Button>
  //       <Button color="primary" onClick={() => {
  //         window.location.reload();
  //       }}>
  //         Login
  //       </Button>
  //     </DialogActions>
  //   </Dialog>
  //   <ValidationDialog />
  // </>) as any


  return (
    <>
        {(lookupsLoaded || ipBlocked) &&
          <RouterProvider router={router} />
        }
        {!loadComplete && !ipBlocked && (
          <LoadingPanel label={"Initializing Application..."} />
        )}
        {
          isGloballySearching && <GlobalSearch />
        }
        <SoftwareUpdateNotifier productName="LOS" />
    </>
  );
}

export default App;
